import { z } from 'zod';
import { securityPolicySchema, type SecurityPolicy } from './staff-settings.schema';
import { STAFF_ROLES } from './staff.schema';

/**
 * Contracts for the workspace Settings API (`/api/admin/settings/*`, `/api/admin/maintenance`, `/api/admin/flags`)
 * and for the staff's own preferences (`/api/staff/me/preferences/*`). Pure Zod so web, mobile and the Worker share
 * exactly the same rules and defaults.
 */

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const weekdaySchema = z.enum(WEEKDAYS);
export type Weekday = z.infer<typeof weekdaySchema>;

/** "Last changed by Priya Sharma · 3 Oct 2026", stamped by the server on every save. */
export const changeStampSchema = z.object({ by: z.string(), at: z.string() });
export type ChangeStamp = z.infer<typeof changeStampSchema>;

// --- Security policy -------------------------------------------------------------------------------------------

export const defaultSecurityPolicy: SecurityPolicy = {
  requireTwoFactor: false,
  appliesTo: [...STAFF_ROLES],
  graceDays: 7,
  sessionDays: 30,
  idleHours: 12,
  reauthMinutes: 10,
  emailDomains: ['mybusinessmate.ai'],
  allowGoogleSignIn: true,
  lockAfterTries: 5,
  lockMinutes: 15,
};

/** The policy plus when two-step became mandatory (starts the grace period), for the Security screen's banner. */
export const securityPolicyResponseSchema = securityPolicySchema.safeExtend({
  requireTwoFactorSince: z.string().optional(),
  changed: changeStampSchema.optional(),
});
export type SecurityPolicyResponse = z.infer<typeof securityPolicyResponseSchema>;

// --- Scheduling rules ------------------------------------------------------------------------------------------

export const STRATEGY_CALL_PLANS = ['Pro only', 'Plus and Pro', 'Everyone'] as const;

export const schedulingRulesSchema = z.object({
  leadMaxActiveTasks: z.number().int().min(1).max(30).default(6),
  specialistMaxActiveTasks: z.number().int().min(1).max(30).default(4),
  warnBeforeLimit: z.boolean().default(true),
  earliestWorkingDays: z.number().int().min(0).max(14).default(2),
  pickableDays: z.array(weekdaySchema).default(['mon', 'tue', 'wed', 'thu', 'fri', 'sat']),
  reschedulesPerMeeting: z.number().int().min(0).max(5).default(1),
  monthlyReviewsPerCycle: z.number().int().min(0).max(4).default(1),
  strategyCallPlan: z.enum(STRATEGY_CALL_PLANS).default('Pro only'),
  freeChaiPerProspect: z.number().int().min(0).max(5).default(1),
  freeChaiVenue: z.string().trim().max(120).default(''),
});
export type SchedulingRules = z.infer<typeof schedulingRulesSchema>;
export const defaultSchedulingRules: SchedulingRules = schedulingRulesSchema.parse({});

// --- Team notifications ----------------------------------------------------------------------------------------

export const teamNotificationRulesSchema = z.object({
  paymentFailed: z.boolean().default(true),
  integrationFailing: z.boolean().default(true),
  taskIdle: z.boolean().default(true),
  clientSilent: z.boolean().default(false),
  digestEnabled: z.boolean().default(true),
  digestRecipients: z.string().trim().max(200).default(''),
  starterEmail: z.string().trim().max(120).default('Assigned, reviews, meetings'),
  starterPush: z.string().trim().max(120).default('Everything'),
  starterQuietHours: z.string().trim().max(60).default('9 PM – 8 AM'),
});
export type TeamNotificationRules = z.infer<typeof teamNotificationRulesSchema>;
export const defaultTeamNotificationRules: TeamNotificationRules = teamNotificationRulesSchema.parse({});

// --- Limits ----------------------------------------------------------------------------------------------------

export const limitRulesSchema = z.object({
  auditsPerVisitorPerDay: z.number().int().min(1).max(50).default(3),
  auditsPerIpPerDay: z.number().int().min(1).max(200).default(10),
  auditPopupWindow: z.string().trim().max(60).default('Last 24 hours'),
  taskFileMb: z.number().int().min(1).max(2000).default(250),
  chatFileMb: z.number().int().min(1).max(2000).default(100),
  allowedFileTypes: z.string().trim().max(120).default('Images, video, PDF, Office, ZIP'),
  brandKitGb: z.number().int().min(1).max(100).default(5),
  signInTriesPer10Min: z.number().int().min(3).max(200).default(20),
});
export type LimitRules = z.infer<typeof limitRulesSchema>;
export const defaultLimitRules: LimitRules = limitRulesSchema.parse({});

// --- Maintenance mode ------------------------------------------------------------------------------------------

export const MAINTENANCE_MESSAGE_MAX = 280;
export const DEFAULT_MAINTENANCE_MESSAGE =
  'We’re making MyBusinessMate better. Your work and data are safe, and we’ll be back shortly.';

export const maintenanceStateSchema = z.object({
  /** off = normal; scheduled = a window is set; on = clients and other staff see the "back soon" page. */
  mode: z.enum(['off', 'scheduled', 'on']),
  window: z.object({ startsAt: z.string(), endsAt: z.string() }).optional(),
  since: z.string().optional(),
  /** When "on" turns itself off. */
  autoOffAt: z.string().optional(),
  message: z.string().trim().min(1).max(MAINTENANCE_MESSAGE_MAX),
  staffKeepWorking: z.boolean(),
  emailWaitingClients: z.boolean(),
  lastChange: changeStampSchema.optional(),
});
export type MaintenanceState = z.infer<typeof maintenanceStateSchema>;

export const defaultMaintenanceState: MaintenanceState = {
  mode: 'off',
  message: DEFAULT_MAINTENANCE_MESSAGE,
  staffKeepWorking: false,
  emailWaitingClients: true,
};

/** What the web app and the mobile app may learn about maintenance without signing in. */
export const publicMaintenanceSchema = z.object({
  /** The state right now, after working out whether a window has started or ended. */
  active: z.boolean(),
  message: z.string(),
  endsAt: z.string().optional(),
  /** A window that has not started yet, for the heads-up banner (shown from 24 hours before). */
  upcoming: z.object({ startsAt: z.string(), endsAt: z.string() }).optional(),
});
export type PublicMaintenance = z.infer<typeof publicMaintenanceSchema>;

/** Each way to change maintenance mode. The server works out the times, so a wrong clock cannot misfire it. */
export const maintenanceCommandSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('turn_on'), autoOffHours: z.number().int().min(1).max(168).nullable().default(1) }),
  z.object({ action: z.literal('turn_off') }),
  z.object({
    action: z.literal('schedule'),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
  }),
  z.object({ action: z.literal('cancel_schedule') }),
  z.object({ action: z.literal('extend'), hours: z.number().int().min(1).max(72) }),
  z.object({
    action: z.literal('settings'),
    message: z.string().trim().min(1, 'Write a short message').max(MAINTENANCE_MESSAGE_MAX).optional(),
    staffKeepWorking: z.boolean().optional(),
    emailWaitingClients: z.boolean().optional(),
  }),
]);
export type MaintenanceCommand = z.input<typeof maintenanceCommandSchema>;

// --- Feature flags ---------------------------------------------------------------------------------------------

export const featureFlagSchema = z.object({
  key: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  audience: z.enum(['everyone', 'share', 'businesses', 'staff']),
  sharePercent: z.number(),
  businessIds: z.array(z.string()),
  alwaysOnForStaff: z.boolean(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type FeatureFlagRecord = z.infer<typeof featureFlagSchema>;

export const featureFlagHistoryEntrySchema = z.object({
  id: z.string(),
  by: z.string(),
  at: z.string(),
  title: z.string(),
  detail: z.string().nullable(),
});
export type FeatureFlagHistoryEntry = z.infer<typeof featureFlagHistoryEntrySchema>;

/** `GET /api/flags`: which flags are on for the signed-in person (clients and staff alike). */
export const evaluatedFlagsSchema = z.record(z.string(), z.boolean());

// --- Staff sign-in health --------------------------------------------------------------------------------------

export const staffHealthRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  staffRole: z.enum(STAFF_ROLES),
  twoFactorEnabled: z.boolean(),
  lockedUntil: z.string().nullable(),
  lastSignInAt: z.string().nullable(),
  deviceCount: z.number(),
});
export type StaffHealthRowRecord = z.infer<typeof staffHealthRowSchema>;

// --- Staff preferences -----------------------------------------------------------------------------------------

const clock = z.string().regex(/^\d{2}:\d{2}$/, 'Use HH:MM');
const isoDate = z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/, 'Use a date');

export const ALERT_IDS = [
  'taskAdded',
  'draftReview',
  'draftDecision',
  'clientMessage',
  'mention',
  'meeting',
  'taskDue',
  'payment',
  'security',
] as const;
export const alertIdSchema = z.enum(ALERT_IDS);
export type AlertId = z.infer<typeof alertIdSchema>;

const channels = z.object({ inApp: z.boolean(), email: z.boolean(), push: z.boolean() });
export type AlertChannels = z.infer<typeof channels>;

export const staffProfilePrefsSchema = z.object({
  displayName: z.string().trim().max(40).default(''),
  phone: z
    .string()
    .trim()
    .refine((v) => v === '' || /^\+?[\d\s-]{8,16}$/.test(v), 'Enter a phone number with the area code')
    .default(''),
  timezone: z.string().min(1).default('Asia/Kolkata'),
  language: z.string().min(1).default('en-IN'),
  dateFormat: z.string().min(1).default('d MMM yyyy'),
  weekStartsOn: z.enum(['monday', 'sunday', 'saturday']).default('monday'),
});
export type StaffProfilePrefs = z.infer<typeof staffProfilePrefsSchema>;

const defaultChannels = (inApp = true, email = true, push = true): AlertChannels => ({ inApp, email, push });
export const defaultAlertChannels: Record<AlertId, AlertChannels> = {
  taskAdded: defaultChannels(),
  draftReview: defaultChannels(),
  draftDecision: defaultChannels(true, false, true),
  clientMessage: defaultChannels(true, false, true),
  mention: defaultChannels(true, false, true),
  meeting: defaultChannels(),
  taskDue: defaultChannels(true, true, false),
  payment: defaultChannels(true, true, false),
  security: defaultChannels(),
};

export const staffNotificationPrefsSchema = z.object({
  dailyDigest: z.boolean().default(true),
  quietHours: z
    .object({
      on: z.boolean(),
      from: clock,
      to: clock,
      days: z.enum(['every-day', 'weekdays', 'weekends']),
    })
    .default({ on: false, from: '21:00', to: '08:00', days: 'every-day' }),
  alerts: z.record(alertIdSchema, channels).default(defaultAlertChannels),
});
export type StaffNotificationPrefs = z.infer<typeof staffNotificationPrefsSchema>;

export const staffAvailabilityPrefsSchema = z.object({
  days: z.array(weekdaySchema).default(['mon', 'tue', 'wed', 'thu', 'fri', 'sat']),
  start: clock.default('10:00'),
  end: clock.default('19:00'),
  outOfOffice: z
    .object({
      on: z.boolean(),
      from: isoDate,
      to: isoDate,
      backup: z.string().trim().max(80),
      note: z.string().trim().max(500),
    })
    .default({ on: false, from: '', to: '', backup: '', note: '' }),
  skills: z.array(z.string().trim().min(1).max(40)).max(30).default([]),
});
export type StaffAvailabilityPrefs = z.infer<typeof staffAvailabilityPrefsSchema>;

export const staffConnectionPrefsSchema = z.object({
  chatName: z.string().trim().max(40).default(''),
  useProfilePhoto: z.boolean().default(true),
});
export type StaffConnectionPrefs = z.infer<typeof staffConnectionPrefsSchema>;

export const STAFF_PREFERENCE_SECTIONS = ['profile', 'notifications', 'availability', 'connections'] as const;
export type StaffPreferenceSection = (typeof STAFF_PREFERENCE_SECTIONS)[number];

export const staffPreferencesSchema = z.object({
  profile: staffProfilePrefsSchema,
  notifications: staffNotificationPrefsSchema,
  availability: staffAvailabilityPrefsSchema,
  connections: staffConnectionPrefsSchema,
});
export type StaffPreferences = z.infer<typeof staffPreferencesSchema>;
export const defaultStaffPreferences: StaffPreferences = staffPreferencesSchema.parse({
  profile: {},
  notifications: {},
  availability: {},
  connections: {},
});

/** The sections a staff member can save, keyed by section name (used to validate `PUT .../preferences/:section`). */
export const staffPreferenceSectionSchemas = {
  profile: staffProfilePrefsSchema,
  notifications: staffNotificationPrefsSchema,
  availability: staffAvailabilityPrefsSchema,
  connections: staffConnectionPrefsSchema,
} as const;

/** The admin-only workspace sections served under `/api/admin/settings/:section`. */
export const WORKSPACE_SECTIONS = ['agency', 'security-policy', 'scheduling', 'team-notifications', 'limits'] as const;
export type WorkspaceSection = (typeof WORKSPACE_SECTIONS)[number];

// --- Pure helpers shared by the API (enforcement) and the web app (display) ------------------------------------

type MaintenanceTiming = Pick<MaintenanceState, 'mode' | 'window' | 'autoOffAt'>;

/**
 * What maintenance mode actually is right now. The stored mode can be stale: a scheduled window starts and ends on
 * its own, and "on" with an automatic end turns itself off. Admins can always sign in either way.
 */
export function effectiveMaintenance(state: MaintenanceTiming, now: Date = new Date()): 'off' | 'scheduled' | 'on' {
  if (state.mode === 'on') {
    return state.autoOffAt && new Date(state.autoOffAt) <= now ? 'off' : 'on';
  }
  if (state.mode === 'scheduled' && state.window) {
    if (new Date(state.window.endsAt) <= now) return 'off';
    if (new Date(state.window.startsAt) <= now) return 'on';
    return 'scheduled';
  }
  return 'off';
}

/** The end time that applies right now, whichever way maintenance was started. */
export function maintenanceEndsAt(state: MaintenanceTiming): string | undefined {
  return state.mode === 'scheduled' ? state.window?.endsAt : state.autoOffAt;
}

/** One sentence per change to a flag, for the flag's history drawer ("raised the share to 20%"). */
export function describeFlagChange(
  before: Pick<FeatureFlagRecord, 'enabled' | 'audience' | 'sharePercent' | 'businessIds' | 'alwaysOnForStaff'>,
  after: Pick<FeatureFlagRecord, 'enabled' | 'audience' | 'sharePercent' | 'businessIds' | 'alwaysOnForStaff'>,
): { title: string; detail?: string }[] {
  const changes: { title: string; detail?: string }[] = [];
  if (before.enabled !== after.enabled) {
    changes.push({ title: after.enabled ? 'turned the flag on' : 'turned the flag off' });
  }
  if (before.audience !== after.audience) {
    const to = {
      everyone: 'everyone',
      share: `${after.sharePercent}% of clients`,
      businesses: `${after.businessIds.length} business${after.businessIds.length === 1 ? '' : 'es'}`,
      staff: 'staff only',
    }[after.audience];
    changes.push({ title: `changed who gets it to ${to}` });
  } else if (after.audience === 'share' && before.sharePercent !== after.sharePercent) {
    changes.push({
      title: `${after.sharePercent > before.sharePercent ? 'raised' : 'lowered'} the share to ${after.sharePercent}%`,
      detail: `Was ${before.sharePercent}%`,
    });
  } else if (after.audience === 'businesses' && before.businessIds.join() !== after.businessIds.join()) {
    changes.push({ title: `changed the businesses to ${after.businessIds.length}` });
  }
  if (before.alwaysOnForStaff !== after.alwaysOnForStaff) {
    changes.push({ title: `turned ${after.alwaysOnForStaff ? 'on' : 'off'} ‘Always on for staff’` });
  }
  return changes;
}
